import { useEffect, useRef } from 'react';
import { useAuth } from './useAuth';

// Singleton EventSource and message handlers
let globalEventSource: EventSource | null = null;
let messageHandlers: Set<(event: MessageEvent) => void> = new Set();
let reconnectAttempts = 0;
let reconnectTimer: NodeJS.Timeout | null = null;
const maxReconnectDelay = 30000; // 30 seconds
const initialDelay = 1000; // 1 second

export function useSSE(onMessage: (event: MessageEvent) => void) {
  const { authStatus } = useAuth();
  const onMessageRef = useRef(onMessage);

  // Keep the message handler ref up to date
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    if (!authStatus.authenticated || !authStatus.currentInstallation) {
      return;
    }

    // Add this handler to the set
    const handler = (event: MessageEvent) => onMessageRef.current(event);
    messageHandlers.add(handler);

    function setupSSE() {
      // Only create connection if it doesn't exist
      if (globalEventSource) {
        return;
      }

      // Clear any existing reconnection timer
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }

      globalEventSource = new EventSource('/events', { withCredentials: true });

      globalEventSource.onopen = () => {
        console.log('SSE connection established');
        reconnectAttempts = 0;
      };

      globalEventSource.onmessage = event => {
        // Broadcast to all handlers
        messageHandlers.forEach(h => h(event));
      };

      globalEventSource.onerror = error => {
        if (globalEventSource?.readyState === EventSource.CLOSED) {
          console.error('SSE connection closed:', error);
          globalEventSource = null;

          // Calculate exponential backoff delay
          const delay = Math.min(initialDelay * Math.pow(2, reconnectAttempts), maxReconnectDelay);

          reconnectAttempts++;
          console.log(`Reconnecting SSE in ${delay}ms (attempt ${reconnectAttempts})...`);

          // Schedule reconnection
          reconnectTimer = setTimeout(() => {
            setupSSE();
          }, delay);
        } else if (globalEventSource?.readyState === EventSource.CONNECTING) {
          console.log('SSE reconnecting...');
        }
      };
    }

    setupSSE();

    return () => {
      // Remove this handler from the set
      messageHandlers.delete(handler);

      // Only close connection if no handlers remain
      if (messageHandlers.size === 0 && globalEventSource) {
        globalEventSource.close();
        globalEventSource = null;
      }
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };
  }, [authStatus.authenticated, authStatus.currentInstallation]);
}
